// On an invoiced quote: take an item off the quote AND its invoice (Stephanie
// 2026-10-29 — e.g. the supplier is out of stock; the sales rep removes it by
// hand). One click, no "are you sure" (2026-11-03). The quote's items are
// otherwise locked once invoiced. Only while the invoice is unpaid and not in
// QuickBooks; after that it's a credit memo.
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { removeFromInvoiceFor, type InvoiceTargets } from "@/lib/invoice-targets";

export function RemoveFromInvoiceButton({
  orderId,
  targets,
  line,
  onDone,
}: {
  orderId: string;
  targets: InvoiceTargets | undefined;
  line: { id: string; name: string };
  onDone: () => void;
}) {
  const [err, setErr] = useState<string | null>(null);
  const choice = removeFromInvoiceFor(targets, line);
  // One click: off the quote and the invoice, invoice total updated — the same
  // as deleting an item on a quote that isn't invoiced (Stephanie 2026-11-03:
  // "if a rep decides to delete an item, it's their responsibility").
  const run = useMutation({
    mutationFn: () => api<{ total: string }>(`/orders/${orderId}/lines/${line.id}/remove-from-invoice`, { method: "POST" }),
    onSuccess: () => {
      setErr(null);
      onDone();
    },
    onError: (e) => setErr(apiErrorMessage(e, "We couldn't remove the item. Nothing was changed — try again.")),
  });
  if (!choice) return null;
  return (
    <span className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => { setErr(null); run.mutate(); }}
        disabled={run.isPending}
        className="text-brown-300 hover:text-rust-500 disabled:opacity-50"
        aria-label={`Remove ${line.name} from the quote and invoice #${choice.invoiceNumber}`}
        title={`Remove from quote & invoice #${choice.invoiceNumber}`}
      >
        <Trash2 className="h-4 w-4" />
      </button>
      {err && <span role="alert" className="absolute right-0 top-6 z-20 w-64 rounded-md border border-rule bg-white p-2 text-left text-xs text-rust-600 shadow">{err}</span>}
    </span>
  );
}
